import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Channel, WsEvent } from '../../shared/models/chat.models';
import { ChatService } from '../../shared/services/chat.service';

type ContactUser = {
  user_id: number;
  user_name: string;
  email: string;
  role_name: string;
};

@Component({
  selector: 'app-channel-list',
  templateUrl: './channel-list.component.html',
  styleUrls: ['./channel-list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, ButtonModule],
})
export class ChannelListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() activeChannelId: number | null = null;
  @Input() viewTab: 'all' | 'chats' | 'channels' | 'contacts' = 'all';
  @Input() variant: 'sidebar' | 'dock' = 'sidebar';
  @Input() showHeader = true;
  @Input() externalSearch = '';
  @Output() channelSelected = new EventEmitter<Channel>();

  @ViewChild('contactsSentinel')
  set contactsSentinel(el: ElementRef<HTMLElement> | undefined) {
    if (el) {
      this.observeContactsSentinel(el.nativeElement);
    } else {
      this.disconnectContactsObserver();
    }
  }

  directChannels: Channel[] = [];
  groupChannels: Channel[] = [];
  entityChannels: Channel[] = [];
  private channelMap = new Map<number, Channel>();
  contactUsers: ContactUser[] = [];

  searchQuery = '';
  loading = false;
  contactsLoading = false;
  contactsLoadingMore = false;
  contactsHasMore = false;

  private readonly contactsPageSize = 50;
  private contactsPage = 1;
  private contactsSearchTerm = '';
  private contactsObserver: IntersectionObserver | null = null;
  private contactsFetchSub: Subscription | null = null;

  // ─── New Channel dialog ────────────────────────────────────────────────────
  showNewChannelDialog = false;
  newChannelName = '';
  creating = false;

  // ─── New DM dialog ─────────────────────────────────────────────────────────
  showNewDmDialog = false;
  dmSearchQuery = '';
  dmSearchResults: ContactUser[] = [];
  dmSearching = false;
  private dmSearch$ = new Subject<string>();
  private contactsInitialized = false;
  private contactSearch$ = new Subject<string>();

  private destroy$ = new Subject<void>();

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadChannels();

    // Listen for WS events to refresh unread counts
    this.chatService.wsEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: WsEvent) => {
        switch (event.type) {
          case 'new_message':
            this.updateChannelOnNewMessage(
              event.channel_id,
              event.message.message_text,
              event.message.created_at
            );
            break;

          case 'mark_read_ack':
            this.clearChannelUnread(event.channel_id);
            break;

          case 'channel_added':
            this.handleChannelAdded(event);
            break;

          case 'member_added':
            this.handleMemberAdded(event);
            break;
        }
      });

    this.contactSearch$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        if (this.viewTab === 'contacts') {
          this.loadContacts(query);
        }
      });

    // Debounced DM user search
    this.dmSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        if (!query || query.length < 2) {
          this.dmSearchResults = [];
          this.dmSearching = false;
          this.cdr.markForCheck();
          return;
        }
        this.dmSearching = true;
        this.cdr.markForCheck();
        this.chatService.searchUsers(query).subscribe({
          next: (results) => {
            this.dmSearchResults = results.users;
            this.dmSearching = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.dmSearching = false;
            this.cdr.markForCheck();
          },
        });
      });
    this.chatService.channelActivity$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        this.updateChannelPreview(
          update.channelId,
          update.lastMessage,
          update.lastMessageAt
        );
      });
  }

  private updateChannelPreview(
    channelId: number,
    lastMessage: string,
    lastMessageAt: string
  ): void {
    const channel = this.channelMap.get(channelId);

    if (!channel) {
      return;
    }

    channel.last_message = lastMessage;
    channel.last_message_at = lastMessageAt;

    channel.unread_count = 0;
    this.chatService.clearUnread(channelId);

    this.moveChannelToTop(channel);
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewTab'] && this.viewTab === 'contacts') {
      if (!this.contactsInitialized) {
        this.contactsInitialized = true;
        this.loadContacts(this.externalSearch?.trim() || '');
      }
    }

    if (
      changes['externalSearch'] &&
      !changes['externalSearch'].firstChange &&
      this.viewTab === 'contacts'
    ) {
      this.contactSearch$.next(this.externalSearch?.trim() || '');
    }
  }

  ngOnDestroy(): void {
    this.contactsFetchSub?.unsubscribe();
    this.disconnectContactsObserver();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── New group channel ─────────────────────────────────────────────────────

  private updateChannelOnNewMessage(
    channelId: number,
    lastMessage: string,
    lastMessageAt: string
  ): void {

    const channel = this.channelMap.get(channelId);

    if (!channel) {
      return;
    }

    channel.last_message = lastMessage;
    channel.last_message_at = lastMessageAt;

    channel.unread_count =
      channelId === this.activeChannelId
        ? 0
        : this.chatService.getUnreadCount(channelId);

    this.moveChannelToTop(channel);
    this.cdr.markForCheck();
  }

  private moveChannelToTop(channel: Channel): void {
    let list: Channel[];

    switch (channel.channel_type) {
      case 'direct':
        list = this.directChannels;
        break;

      case 'group':
        list = this.groupChannels;
        break;

      case 'entity_linked':
        list = this.entityChannels;
        break;

      default:
        return;
    }

    const index = list.indexOf(channel);

    if (index > 0) {
      list.splice(index, 1);
      list.unshift(channel);
    }
  }

  private clearChannelUnread(channelId: number): void {
    const channel = this.channelMap.get(channelId);

    if (!channel) {
      this.chatService.clearUnread(channelId);
      return;
    }

    channel.unread_count = 0;
    this.chatService.clearUnread(channelId);
    this.cdr.markForCheck();
  }

  private handleChannelAdded(event: any): void {
    const exists =
      this.directChannels.some(c => c.id === event.channel_id) ||
      this.groupChannels.some(c => c.id === event.channel_id) ||
      this.entityChannels.some(c => c.id === event.channel_id);

    if (exists) {
      return;
    }

    // Fetch the complete channel details
    this.chatService.getChannelDetail(event.channel_id).subscribe({
      next: (detail) => {
        const channel = detail.channel;
        this.channelMap.set(channel.id, channel);
        switch (channel.channel_type) {
          case 'direct':
            this.directChannels.unshift(channel);
            break;

          case 'group':
            this.groupChannels.unshift(channel);
            break;

          case 'entity_linked':
            this.entityChannels.unshift(channel);
            break;
        }

        // Subscribe to the new channel
        this.chatService.subscribeToChannel(channel.id);

        channel.unread_count =
          this.chatService.getUnreadCount(channel.id) ||
          channel.unread_count ||
          0;

        this.cdr.markForCheck();
      }
    });
  }

  private handleMemberAdded(event: any): void {
    const channel = this.channelMap.get(event.channel_id);

    if (!channel) {
      return;
    }

    channel.member_count = (channel.member_count || 0) + event.user_ids.length;

    this.cdr.markForCheck();
  }

  openNewChannelDialog(): void {
    this.newChannelName = '';
    this.showNewChannelDialog = true;
  }

  createNewChannel(): void {
    const name = this.newChannelName.trim();
    if (!name || this.creating) return;
    this.creating = true;
    this.cdr.markForCheck();
    this.chatService
      .createChannel({ channel_type: 'group', channel_name: name, member_ids: [] })
      .subscribe({
        next: (res) => {
          this.creating = false;
          this.showNewChannelDialog = false;
          // this.loadChannels();
          // Auto-open the new channel
          // this.chatService.getChannelDetail(res.channel_id).subscribe({
          //   next: (detail) => this.channelSelected.emit(detail.channel),
          // });
          this.creating = false;
          this.showNewChannelDialog = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.creating = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ─── New DM ────────────────────────────────────────────────────────────────

  openNewDmDialog(): void {
    this.dmSearchQuery = '';
    this.dmSearchResults = [];
    this.showNewDmDialog = true;
  }

  onDmSearchInput(): void {
    this.dmSearch$.next(this.dmSearchQuery);
  }

  startDm(user: { user_id: number; user_name: string; email: string }): void {
    this.creating = true;
    this.cdr.markForCheck();
    this.chatService
      .createChannel({
        channel_type: 'direct',
        channel_name: user.user_name,
        member_ids: [user.user_id],
      })
      .subscribe({
        next: (res) => {
          this.creating = false;
          this.showNewDmDialog = false;
          // this.loadChannels();
          // this.chatService.getChannelDetail(res.channel_id).subscribe({
          //   next: (detail) => this.channelSelected.emit(detail.channel),
          // });
          this.creating = false;
          this.showNewChannelDialog = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.creating = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadChannels(): void {
    this.loading = true;
    this.chatService
      .getMyChannels()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          res.channels.forEach((ch) => {
            const live = this.chatService.getUnreadCount(ch.id);
            if (live > (ch.unread_count || 0)) {
              ch.unread_count = live;
            }
          });
          this.chatService.updateUnreadCounts(res.channels);
          // Auto-subscribe to every channel so WS events arrive even when not actively viewing
          res.channels.forEach((ch) => this.chatService.subscribeToChannel(ch.id));
          this.partitionChannels(res.channels);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private partitionChannels(channels: Channel[]): void {
    this.directChannels = channels.filter(c => c.channel_type === 'direct');
    this.groupChannels = channels.filter(c => c.channel_type === 'group');
    this.entityChannels = channels.filter(c => c.channel_type === 'entity_linked');

    this.channelMap.clear();

    channels.forEach(channel => {
      this.channelMap.set(channel.id, channel);
    });
  }

  selectChannel(channel: Channel): void {
    this.activeChannelId = channel.id;
    channel.unread_count = 0;
    this.chatService.clearUnread(channel.id);
    this.channelSelected.emit(channel);
    this.cdr.markForCheck();
  }

  getChannelDisplayName(channel: Channel): string {
    if (channel.channel_name) return channel.channel_name;
    if (channel.channel_type === 'direct') return `DM #${channel.id}`;
    if (channel.channel_type === 'entity_linked') {
      return `${channel.linked_entity_type} #${channel.linked_entity_id}`;
    }
    return `Channel #${channel.id}`;
  }

  getChannelIcon(channel: Channel): string {
    if (channel.channel_type === 'direct') return 'pi pi-user';
    if (channel.channel_type === 'entity_linked') return 'pi pi-tag';
    return 'pi pi-comments';
  }

  get effectiveSearch(): string {
    return this.externalSearch || this.searchQuery;
  }

  get filteredDirect(): Channel[] {
    return this.filterList(this.directChannels);
  }

  get filteredGroup(): Channel[] {
    return this.filterList(this.groupChannels);
  }

  get filteredEntity(): Channel[] {
    return this.filterList(this.entityChannels);
  }

  // get filteredContacts(): { user_id: number; user_name: string; email: string }[] {
  //   if (!this.effectiveSearch) return this.contactUsers;
  //   const q = this.effectiveSearch.toLowerCase();
  //   return this.contactUsers.filter(
  //     (u) =>
  //       u.user_name.toLowerCase().includes(q) ||
  //       (u.email && u.email.toLowerCase().includes(q))
  //     );
  // }

  get filteredContacts() {
    return this.contactUsers;
  }

  get showChatsSection(): boolean {
    return this.viewTab === 'all' || this.viewTab === 'chats';
  }

  get showChannelsSection(): boolean {
    return this.viewTab === 'all' || this.viewTab === 'channels';
  }

  get showContactsSection(): boolean {
    return this.viewTab === 'contacts';
  }

  loadContacts(searchTerm: string = '', append: boolean = false): void {
    if (append) {
      if (!this.contactsHasMore || this.contactsLoading || this.contactsLoadingMore) {
        return;
      }
      this.contactsLoadingMore = true;
    } else {
      this.contactsFetchSub?.unsubscribe();
      this.contactsSearchTerm = searchTerm;
      this.contactsPage = 1;
      this.contactsHasMore = false;
      this.contactsLoading = true;
      this.contactsLoadingMore = false;
    }

    this.cdr.markForCheck();

    const pageToLoad = append ? this.contactsPage + 1 : 1;

    this.contactsFetchSub = this.chatService
      .searchUsers(this.contactsSearchTerm, pageToLoad, this.contactsPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.contactUsers = append
            ? [...this.contactUsers, ...results.users]
            : results.users;
          this.contactsPage = pageToLoad;
          this.contactsHasMore = !results.last;
          this.contactsLoading = false;
          this.contactsLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (!append) {
            this.contactUsers = [];
            this.contactsHasMore = false;
          }
          this.contactsLoading = false;
          this.contactsLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private observeContactsSentinel(element: HTMLElement): void {
    this.disconnectContactsObserver();

    const root = element.closest('.cl-scroll');
    this.contactsObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadContacts(this.contactsSearchTerm, true);
        }
      },
      {
        root: root instanceof Element ? root : null,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    this.contactsObserver.observe(element);
  }

  private disconnectContactsObserver(): void {
    this.contactsObserver?.disconnect();
    this.contactsObserver = null;
  }

  onContactClick(user: { user_id: number; user_name: string; email: string }): void {
    const existing = this.directChannels.find((ch) =>
      ch.channel_name?.toLowerCase() === user.user_name.toLowerCase()
    );
    if (existing) {
      this.selectChannel(existing);
      return;
    }
    this.startDm(user);
  }

  private filterList(channels: Channel[]): Channel[] {
    const q = this.normalizeSearch(this.effectiveSearch);

    if (!q) return channels;

    return channels.filter((channel) => {
      const channelName = this.normalizeSearch(
        this.getChannelDisplayName(channel)
      );

      return channelName.includes(q);
    });
  }

  private normalizeSearch(value: string | null | undefined): string {
    return (value || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
  }
}
